// The fixed choices on the member's golfer profile. Mirrors the backend:
//   GolfPlayed.Domain.Enums.GamblePreference and PaceOfPlay (sent as their int values, 1 based),
//   GolfPlayed.Domain.Helpers.Constants.LanguageConstants (ISO 639-1 codes).
// Add a language to both lists, or the backend refuses it on save.

export interface ProfileChoice {
  value: number;
  label: string;
}

export const GAMBLE_PREFERENCES: ProfileChoice[] = [
  { value: 1, label: 'Definitely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'No' },
];

export const PACES_OF_PLAY: ProfileChoice[] = [
  { value: 1, label: 'Fast' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'Leisure' },
];

export const LANGUAGES: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'af', label: 'Afrikaans' },
  { code: 'zu', label: 'isiZulu' },
  { code: 'xh', label: 'isiXhosa' },
  { code: 'st', label: 'Sesotho' },
  { code: 'tn', label: 'Setswana' },
  { code: 'nso', label: 'Sepedi' },
  { code: 'ts', label: 'Xitsonga' },
  { code: 'ss', label: 'siSwati' },
  { code: 've', label: 'Tshivenda' },
  { code: 'nr', label: 'isiNdebele' },
  { code: 'nl', label: 'Dutch' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'el', label: 'Greek' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ar', label: 'Arabic' },
];

export const ABOUT_MAX_LENGTH = 200;

// WHS bounds, same as MemberProfileService. A plus handicap is stored negative.
export const HANDICAP_INDEX_MIN = -10;
export const HANDICAP_INDEX_MAX = 54;

export const choiceLabel = (
  choices: ProfileChoice[],
  value: number | undefined | null,
): string | null => choices.find((c) => c.value === value)?.label ?? null;

export const languageLabels = (codes: string[] | undefined): string[] =>
  (codes ?? []).map(
    (code) => LANGUAGES.find((l) => l.code === code)?.label ?? code,
  );

// A plus handicap reads "+2.4", the way golfers write it. Stored as -2.4.
export const formatHandicapIndex = (
  index: number | undefined | null,
): string | null => {
  if (index == null) {
    return null;
  }
  return index < 0 ? `+${Math.abs(index).toFixed(1)}` : index.toFixed(1);
};

// The member's own profile, and its Settings page (the phone's way to edit it, behind the gear).
export const PROFILE_ROUTE = '/member/profile';
export const PROFILE_SETTINGS_ROUTE = `${PROFILE_ROUTE}/settings`;

// The member's golf lists (courses, countries, states, bucket list, home courses) on one page,
// one tab each. The profile's counters open it on the matching tab.
export const PROFILE_GOLF_ROUTE = `${PROFILE_ROUTE}/golf`;
export const GOLF_TAB_IDS = [
  'courses',
  'countries',
  'states',
  'bucket',
  'home',
] as const;
export type GolfTabId = (typeof GOLF_TAB_IDS)[number];
export const golfTabRoute = (tab: GolfTabId): string =>
  `${PROFILE_GOLF_ROUTE}?tab=${tab}`;
